/**
 * Frontend Analytics Service
 *
 * Provides a typed, centralized interface for tracking user events via PostHog.
 * Following the Philosophy of Software Design principle: deep modules with simple interfaces.
 *
 * Usage:
 * ```tsx
 * import { useAnalytics } from '@/services/frontendAnalyticsService';
 *
 * function MyComponent() {
 *   const analytics = useAnalytics();
 *
 *   const handleClick = () => {
 *     analytics.periksaKontrak.fileUploaded({ fileCount: 2, totalSize: 1024000 });
 *   };
 * }
 * ```
 */

import { usePostHog } from 'posthog-js/react';
import {
  createEarlyAccessAnalytics,
  createFeaturesAnalytics,
  createJudgementSummarizationAnalytics,
  createPeriksaKontrakAnalytics,
  createRegulationSummarizationAnalytics,
} from './frontendAnalyticsFactories';
import {
  createBlogAnalytics,
  createLandingPageAnalytics,
} from './frontendAnalyticsLandingFactories';

export type { PeriksaKontrakAnalytics } from './frontendAnalyticsFactories';

// ============================================================================
// Public Hooks
// ============================================================================

/**
 * Main hook for accessing analytics in Periksa Kontrak pages
 *
 * @example
 * ```tsx
 * const analytics = useAnalytics();
 * analytics.periksaKontrak.fileUploaded({ fileCount: 2, totalSize: 1024000 });
 * ```
 */
export function useAnalytics() {
  const posthog = usePostHog();

  return {
    periksaKontrak: createPeriksaKontrakAnalytics(posthog),
    earlyAccess: createEarlyAccessAnalytics(posthog),
    judgementSummarization: createJudgementSummarizationAnalytics(posthog),
    regulationSummarization: createRegulationSummarizationAnalytics(posthog),
    features: createFeaturesAnalytics(posthog),
    blog: createBlogAnalytics(posthog),
    landingPage: createLandingPageAnalytics(posthog),

    /**
     * Identify a user for analytics tracking
     * Call this when you have user information (e.g., after email submission)
     */
    identify: (userId: string, properties?: Record<string, any>) => {
      if (posthog) {
        posthog.identify(userId, properties);
      }
    },

    /**
     * Reset analytics on logout or session end
     */
    reset: () => {
      if (posthog) {
        posthog.reset();
      }
    },
  };
}

/**
 * Utility hook to extract email domain for privacy-safe analytics
 */
export function getEmailDomain(email: string): string {
  const parts = email.split('@');
  return parts.length === 2 ? parts[1] : '';
}

/**
 * Utility to calculate upload duration
 */
export function createTimingTracker() {
  const startTime = Date.now();

  return {
    getDuration: () => Date.now() - startTime,
  };
}
